import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { qk } from '../keys';
import type { OfficeUser, Paginated } from '../types';

/** Профилите на фирмата — без шофьорите (те се свързват с код от „Шофьори"). */
export function useUsers(includeInactive = false) {
  return useQuery({
    queryKey: qk.users.list(includeInactive),
    /** `includeInactive=false` в адреса сървърът чете като „да" (z.coerce.boolean) — пращаме го само при „да". */
    queryFn: () => api.get<Paginated<OfficeUser>>('/users', { perPage: 100, ...(includeInactive ? { includeInactive: true } : {}) }),
  });
}

function useUsersMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.users.all }),
  });
}

export function useCreateUser() {
  return useUsersMutation(
    (input: { fullName: string; role: string; email: string; phone?: string; password: string }) =>
      api.post<OfficeUser>('/users', input),
  );
}

export function useUpdateUser() {
  return useUsersMutation((input: { id: string; isActive?: boolean; fullName?: string; phone?: string | null }) => {
    const { id, ...body } = input;
    return api.patch<OfficeUser>(`/users/${id}`, body);
  });
}

export function useResetPassword() {
  return useUsersMutation((input: { id: string; newPassword: string }) =>
    api.post<void>(`/users/${input.id}/password`, { newPassword: input.newPassword }),
  );
}
